<template>
  <template v-if="video.type === 'youtube'">
    <div class="flex flex-col grow basis-0">
      <div class="aspect-video lg:w-full relative">
        <iframe
          class="absolute top-0 left-0 w-full h-full"
          :src="`https://www.youtube.com/embed/${video.video.id}`"
          title="YouTube video player"
          frameborder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerpolicy="strict-origin-when-cross-origin"
          allowfullscreen
        ></iframe>
      </div>
      <div class="flex gap-5 justify-between mt-5">
        <div>
          <span
            class="uppercase text-xs tracking-[.2rem] flex gap-4 items-center mb-3"
          >
            YouTube - {{ formatDate(video.video.createdAt) }}
          </span>
          <HeadingBasic :size="3">{{ video.video.title }}</HeadingBasic>
          <p class="text-gray-500 dark:text-gray-400 mt-4">
            {{ video.video.description }}
          </p>
        </div>
        <a
          :href="`https://www.youtube.com/watch?v=${video.video.id}`"
          target="_blank"
          rel="noopener noreferrer"
          class="text-white hover:text-cardinal dark:hover:text-cardinal-100 mt-2 inline-block"
        >
          <i class="fab fa-youtube text-3xl"></i>
        </a>
      </div>
    </div>
  </template>

  <template v-else-if="video.type === 'instagram'">
    <div class="flex flex-col grow basis-0">
      <div class="aspect-video lg:w-full relative">
        <video
          ref="videoRef"
          class="absolute top-0 left-0 w-full h-full"
          preload="metadata"
          :poster="video.video.thumbnail_storage_url"
          @play="isPlaying = true"
          @pause="isPlaying = false"
          :controls="isPlaying"
        >
          <source :src="video.video.media_storage_url" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div
          v-show="!isPlaying"
          class="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center cursor-pointer transition-opacity duration-300"
          @click="playVideo"
        >
          <div
            class="h-16 w-16 bg-black bg-opacity-40 rounded-full flex items-center justify-center transition-colors hover:bg-opacity-60"
          >
            <i
              class="fas fa-play text-white text-2xl hover:text-gray-200 transition-colors duration-200"
            ></i>
          </div>
        </div>
      </div>
      <div class="flex gap-5 justify-between mt-5">
        <div>
          <span
            class="uppercase text-xs tracking-[.2rem] flex gap-4 items-center mb-3"
          >
            Instagram - {{ formatDate(video.video.created_at) }}
          </span>
          <p class="text-gray-500 dark:text-gray-400">
            {{ video.video.caption }}
          </p>
        </div>
        <a
          :href="video.video.permalink"
          target="_blank"
          rel="noopener noreferrer"
          class="text-cardinal hover:text-cardinal-700 dark:text-cardinal-300 dark:hover:text-cardinal-100 mt-2 inline-block"
        >
          <i
            class="fab fa-instagram text-gray-500 dark:text-gray-200 text-3xl"
          ></i>
        </a>
      </div>
    </div>
  </template>
</template>

<script lang="ts" setup>
import type { Video } from '~/types/videos';
import { ref } from 'vue';

defineProps<{
  video: Video;
}>();

const videoRef = ref<HTMLVideoElement | null>(null);
const isPlaying = ref(false);

const playVideo = () => {
  if (videoRef.value) {
    videoRef.value.play();
  }
};
</script>
